<template>
  <component
    :is="dialog ? 'q-dialog' : 'div'"
    v-model="shown"
    data-test-id="global-widget-settings-form"
  >
    <q-card
      :style="$q.screen.gt.xs && dialog ? 'max-height: calc(100vh - 100px); margin-bottom: 50px; max-width: 600px' : undefined"
    >
      <q-card-section
        v-if="dialog"
        class="row items-center q-mx-xs"
      >
        <div class="text-h6">
          Global Widget Settings
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section
        :class="compactView ? 'q-pa-sm' : undefined"
      >
        <div
          class="row items-start"
          :class="compactView ? 'q-col-gutter-sm' : 'q-col-gutter-md'"
        >
          <div
            class="text-bold"
            :class="compactView ? 'col-8 q-pt-md' : 'col-12'"
          >
            Visiblity
          </div>
          <q-toggle
            v-model="globalWidget.config.on"
            :class="compactView ? 'col-4 q-pb-none' : 'col-3'"
            keep-color
            :color="globalWidget.config.on ? 'positive' : 'grey'"
            unchecked-icon="clear"
            checked-icon="check"
            label="On"
          />
          <q-select
            v-model="globalWidget.config.visible_for"
            :class="compactView ? 'col-12 q-pb-xs' : 'col-9'"
            label="Visible For"
            :options="visibilityOptions"
            bottom-slots
            map-options
            emit-value
            use-chips
            clearable
            multiple
          />
          <iso-date-input
            v-model="globalWidget.config.startDate"
            class="col-6"
            :class="compactView ? 'q-pb-xs' : undefined"
            label="Start Date"
            clearable
            :rules="[
              () => !globalWidget.config.startDate || !globalWidget.config.endDate || globalWidget.config.startDate <= globalWidget.config.endDate || 'Date cannot postdate end date',
            ]"
          />
          <iso-date-input
            v-model="globalWidget.config.endDate"
            class="col-6"
            :class="compactView ? 'q-pb-xs' : undefined"
            label="End Date"
            clearable
            :rules="[
              () => !globalWidget.config.startDate || !globalWidget.config.endDate || globalWidget.config.endDate >= globalWidget.config.startDate || 'Date cannot predate start date',
            ]"
          />
          <q-select
            :model-value="globalWidget.config.daysOfWeek"
            multiple
            use-chips
            :class="compactView ? 'col-12 q-pb-xs' : 'col-12'"
            label="Days of Week"
            :options="$constants.dayOfWeekNumbers"
            map-options
            emit-value
            bottom-slots
            @update:model-value="handleUpdateDaysOfWeek"
          />
          <q-toggle
            v-if="globalWidget.type === GLOBAL_WIDGET_TYPES.BANNER"
            v-model="globalWidget.config.isDismissable"
            :class="compactView ? 'col-12 q-pb-xs' : 'col-3'"
            :color="globalWidget.config.isDismissable ? 'positive' : 'grey'"
            unchecked-icon="clear"
            checked-icon="check"
            label="Allow Dismiss"
          />
          <q-select
            v-if="globalWidget.type === GLOBAL_WIDGET_TYPES.BANNER"
            v-model="globalWidget.config.restorationFrequency"
            :class="compactView ? 'col-12 q-pb-sm' : 'col-9'"
            :options="restorationFrequencyOptions"
            bottom-slots
            map-options
            emit-value
            clearable
            :disable="!globalWidget.config.isDismissable"
            label="Restoration Frequency"
          >
            <q-tooltip>
              The frequency for restoring a dismissed global widget.
            </q-tooltip>
          </q-select>

          <q-toggle

            v-model="globalWidget.config.pageRestrictionsEnabled"
            :class="compactView ? 'col-12 q-pb-xs' : 'col-3'"
            :color="globalWidget.config.pageRestrictionsEnabled ? 'positive' : 'grey'"
            unchecked-icon="clear"
            checked-icon="check"
            label="Restrictions by Pages"
          />
          <q-select
            v-model="globalWidget.visiblePageIds"
            :class="compactView ? 'col-7 q-pb-sm' : 'col-6'"
            :label="!!globalWidget.config.pageRestrictionsExclude ? 'Exclude Pages' : 'Include Pages'"
            :options="$_.unionBy(pages.data, globalWidget.visiblePages.data, 'id')"
            option-label="title"
            option-value="id"
            map-options
            emit-value
            multiple
            use-chips
            use-input
            input-style="search"
            input-debounce="300"
            :disable="!globalWidget.config.pageRestrictionsEnabled"
            @filter="filterPages"
          />
          <q-toggle
            v-model="globalWidget.config.pageRestrictionsExclude"
            :class="compactView ? 'col-5 q-pb-xs' : 'col-3'"
            :color="globalWidget.config.pageRestrictionsExclude ? 'positive' : 'grey'"
            unchecked-icon="clear"
            checked-icon="check"
            label="Exclude"
            bottom-slots
            :disable="!globalWidget.config.pageRestrictionsEnabled"
          />
        </div>
      </q-card-section>
      <q-card-section :class="{ 'q-pa-sm': compactView }">
        <div class="text-bold q-mb-sm">
          Styling
        </div>
        <div
          class="row items-start"
          :class="compactView ? 'q-col-gutter-sm' : 'q-col-gutter-md'"
        >
          <q-select
            v-model="globalWidget.config.widthMode"
            class="col-6"
            label="Width Mode"
            :options="widthModeOptions"
            map-options
            emit-value
          />
          <q-select
            v-model="globalWidget.config.marginSides"
            class="col-6"
            label="Margin Sides"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <q-select
            v-model="globalWidget.config.marginTop"
            class="col-6"
            label="Margin Top"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <q-select
            v-model="globalWidget.config.marginBottom"
            class="col-6"
            label="Margin Bottom"
            map-options
            emit-value
            clearable
            :options="styleSizeOptions"
          />
          <color-selection
            :color-name="globalWidget.config.backgroundColor"
            :custom-color-hex="globalWidget.config.backgroundColorHex"
            :label="'Background Color'"
            class="full-width"
            :compact-view="compactView"
            @update:model-value="handleBackgroundColorInput"
          />
          <template v-if="globalWidget.config.backgroundColor === 'custom'">
            <image-uploader
              v-model="globalWidget.config.backgroundImage"
              :class="compactView ? 'col-12 overflow-hidden' : 'col-6'"
              min-height="190px"
              max-height="200px"
              :image-url="globalWidget.backgroundImageUrl"
              :model="globalWidget"
            />
            <div
              class="column justify-between q-gutter-sm"
              :class="compactView ? 'col-12' : 'col-6'"
            >
              <q-select
                v-model="globalWidget.config.backgroundImageFit"
                :options="IMAGE_TRANSFORM_OPTIONS.ALL"
                :option-label="type => $_.startCase(type)"
                map-options
                emit-value
                label="Image Fit"
                class="col"
                :dense="compactView"
              />
              <q-select
                v-model="globalWidget.config.backgroundImagePosition"
                :options="IMAGE_POSITION_OPTIONS.ALL"
                :option-label="type => $_.startCase(type)"
                map-options
                emit-value
                label="Image Position"
                class="col"
              />
              <div class="row col">
                <q-select
                  v-model="globalWidget.config.backgroundRepeat"
                  :options="backgroundRepeatOptions"
                  map-options
                  emit-value
                  label="Repeat"
                  class="col q-pr-sm"
                />
                <q-select
                  v-model="globalWidget.config.backgroundAttachment"
                  :options="backgroundAttachmentOptions"
                  map-options
                  emit-value
                  label="Attachment"
                  class="col"
                />
              </div>
            </div>
          </template>
        </div>
      </q-card-section>
      <q-card-section :class="{ 'q-pa-sm': compactView }">
        <div class="text-bold q-mb-sm">
          Advanced Styling
        </div>
        <div
          class="row items-start"
          :class="compactView ? 'q-col-gutter-sm' : 'q-col-gutter-md'"
        >
          <q-input
            v-model="globalWidget.config.class"
            class="col-6"
            label="Classes"
            clearable
          />
          <q-input
            v-model="globalWidget.config.style"
            class="col-6"
            label="Styles"
            clearable
          />
        </div>
        <div
          class="row"
          :class="compactView ? 'q-col-gutter-sm q-mt-xs' : 'q-col-gutter-md q-mt-sm'"
        >
          <q-input
            v-model="globalWidget.styles"
            type="textarea"
            class="col-12"
            label="Custom CSS"
          />
        </div>
      </q-card-section>
    </q-card>
  </component>
</template>

<script>
import { IMAGE_POSITION_OPTIONS, IMAGE_TRANSFORM_OPTIONS } from 'src/models/carousel/Carousel'
import GlobalWidget, { GLOBAL_WIDGET_TYPES, RESTORATION_FREQUENCY_OPTIONS, VISIBILITY_OPTIONS, WIDTH_MODE_OPTIONS } from 'src/models/global-widget/GlobalWidget'
import { IsoDate } from 'src/models/model'
import { backgroundAttachmentOptions, backgroundRepeatOptions, styleSizeOptions } from 'src/models/page/Section'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import IsoDateInput from 'src/modules/admin/components/IsoDateInput'

export default {
  name: 'GlobalWidgetGenericSettingsForm',
  components: {
    IsoDateInput,
    ColorSelection,
    ImageUploader
  },
  configuration: ['currentCircuit'],
  props: {
    globalWidget: {
      type: GlobalWidget,
      required: true
    },
    dialog: {
      type: Boolean,
      default: true
    },
    compactView: {
      type: Boolean,
      default: false
    }
  },
  models: {
    pages: {
      fields: ['id', 'title'],
      variables () {
        return {
          searchString: this.pageSearchString,
          limit: 10
        }
      }
    }
  },
  data () {
    return {
      shown: false,
      pageSearchString: null
    }
  },
  created () {
    _.extend(this, {
      IsoDate,
      visibilityOptions: this.$mh.asOptions(VISIBILITY_OPTIONS.ALL),
      restorationFrequencyOptions: this.$mh.asOptions(RESTORATION_FREQUENCY_OPTIONS),
      widthModeOptions: this.$mh.asOptions(WIDTH_MODE_OPTIONS.ALL),
      styleSizeOptions,
      IMAGE_TRANSFORM_OPTIONS,
      IMAGE_POSITION_OPTIONS,
      backgroundRepeatOptions,
      backgroundAttachmentOptions,
      GLOBAL_WIDGET_TYPES
    })
  },
  methods: {
    show () {
      this.shown = true
    },
    handleUpdateDaysOfWeek (value) {
      // Preserved the circuit's ordering of the days of the week
      this.globalWidget.config.daysOfWeek = _.intersection(this.currentCircuit.daysOfWeekNumbers, value)
    },
    handleBackgroundColorInput (colorName, customColorHex) {
      this.globalWidget.config.backgroundColor = colorName
      this.globalWidget.config.backgroundColorHex = customColorHex
    },
    filterPages (val, update) {
      update(() => {
        this.pageSearchString = _.isEmpty(val) ? null : val.trim()
      })
    }
  }
}

</script>
